import { useMemo, useState } from 'react';
import { useData } from '../AdoptionData.jsx';
import {
  stickiness, sessionMetrics, intensity, cohortRetention, growthAccounting,
  activation, concentration, accountHealth, opsMetrics, companyOf,
} from '../../../../lib/adoptionAnalytics.js';
import { MiniLine, GrowthBars, CohortHeatmap } from '../charts.jsx';
import { Kpi, Kpis } from '../Kpi.jsx';

export default function Metrics() {
  const { events, aliases } = useData();
  const [scope, setScope] = useState('__ALL__');

  const companies = useMemo(
    () => [...new Set(events.map((e) => companyOf(e.user, aliases)))].sort(),
    [events, aliases]
  );
  const ev = useMemo(
    () => (scope === '__ALL__' ? events : events.filter((e) => companyOf(e.user, aliases) === scope)),
    [events, aliases, scope]
  );

  const stk = useMemo(() => stickiness(ev), [ev]);
  const sess = useMemo(() => sessionMetrics(ev), [ev]);
  const inten = useMemo(() => intensity(ev), [ev]);
  const cohort = useMemo(() => cohortRetention(ev), [ev]);
  const growth = useMemo(() => growthAccounting(ev), [ev]);
  const act = useMemo(() => activation(ev), [ev]);
  const conc = useMemo(() => concentration(ev, { aliases }), [ev, aliases]);
  const accounts = useMemo(() => accountHealth(ev, { aliases }), [ev, aliases]);
  const ops = useMemo(() => opsMetrics(ev), [ev]);

  if (!ev.length) return <div className="status">No activity in this snapshot.</div>;

  return (
    <>
      <div className="card-head">
        <h1>Product metrics</h1>
        <select value={scope} onChange={(e) => setScope(e.target.value)}>
          <option value="__ALL__">All companies</option>
          {companies.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <h2 className="section-rule">Engagement</h2>
      <Kpis>
        <Kpi v={stk.stickiness} l="Stickiness (WAU/MAU)" />
        <Kpi v={stk.avgWau} l="Avg weekly active users" />
        <Kpi v={stk.avgMau} l="Avg monthly active (4-wk)" />
        <Kpi v={sess.medianQueriesPerSession} l="Median queries / session" />
        <Kpi v={`${sess.multiQueryPct}%`} l="Multi-query sessions" />
        <Kpi v={`${sess.medianDurationMin}m`} l="Median session length" />
        <Kpi v={inten.medianPerUserWeek} l="Median queries / user / wk" />
        <Kpi v={sess.avgSessionsPerUser} l="Avg sessions / user" />
      </Kpis>

      <h2 className="section-rule">Retention</h2>
      <section className="card">
        <h3>Cohort retention — % of each week's new users active N weeks later</h3>
        <CohortHeatmap cohort={cohort} maxK={12} />
      </section>
      <section className="card">
        <div className="card-head">
          <h3>Growth accounting (active users)</h3>
          <div className="kpi-inline">Quick Ratio <b>{growth.quickRatio ?? '—'}</b></div>
        </div>
        <p className="desc">Up = new + retained + resurrected · down = churned. Quick Ratio = (new+resurrected) ÷ churned; &gt;1 means growing.</p>
        <GrowthBars g={growth} />
        <div className="legend">
          <span className="leg"><span className="sw" style={{ background: '#37c98b' }} />New</span>
          <span className="leg"><span className="sw" style={{ background: '#5b8def' }} />Retained</span>
          <span className="leg"><span className="sw" style={{ background: '#f5a623' }} />Resurrected</span>
          <span className="leg"><span className="sw" style={{ background: '#e9576b' }} />Churned</span>
        </div>
      </section>

      <h2 className="section-rule">Activation</h2>
      <Kpis>
        <Kpi v={`${act.activationRate}%`} l={`Activated (≥${act.valueN} queries)`} />
        <Kpi v={act.medianDaysToValue == null ? '—' : `${act.medianDaysToValue}d`} l="Median time-to-value" />
        <Kpi v={act.w1Retention == null ? '—' : `${act.w1Retention}%`} l="Week-1 retention (new users)" />
        <Kpi v={`${act.activatedUsers}/${act.totalUsers}`} l="Activated / total users" />
      </Kpis>

      <h2 className="section-rule">Concentration &amp; risk</h2>
      <Kpis>
        <Kpi v={conc.topAccount} l="Top account" small />
        <Kpi v={`${conc.topAccountSharePct}%`} l="Top-account share of usage" />
        <Kpi v={`${conc.topDecileUserShare}%`} l="Top-10% users' share" />
        <Kpi v={conc.hhi.length ? conc.hhi[conc.hhi.length - 1] : '—'} l="Account HHI (latest wk)" />
      </Kpis>
      <section className="card">
        <h3>Top-account share of weekly usage (%)</h3>
        <p className="desc">Higher = more dependent on a single customer. Watch for sustained &gt;60%.</p>
        <MiniLine weeks={conc.weeks} values={conc.top1} max={100} unit="%" color="#e9576b" />
      </section>

      <h2 className="section-rule">Account health</h2>
      <section className="card">
        <div className="table-wrap">
          <table className="tbl">
            <thead><tr><th>Company</th><th className="r">Users</th><th>Threading</th><th className="r">Interactions</th><th>First</th><th>Last</th><th className="r">Wks idle</th><th>Status</th></tr></thead>
            <tbody>
              {accounts.map((a) => (
                <tr key={a.company}>
                  <td>{a.company}</td>
                  <td className="r">{a.users}</td>
                  <td>{a.threading === 'multi' ? 'multi-threaded' : 'single-threaded'}</td>
                  <td className="r">{a.interactions}</td>
                  <td>{a.firstActive}</td>
                  <td>{a.lastActive}</td>
                  <td className="r">{a.weeksSinceActive}</td>
                  <td>{a.dormant ? <span className="badge-dormant">dormant</span> : <span className="badge-active">active</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <h2 className="section-rule">Quality &amp; ops</h2>
      <Kpis>
        <Kpi v={ops.p50Ms == null ? '—' : `${ops.p50Ms} ms`} l="API latency p50" />
        <Kpi v={ops.p95Ms == null ? '—' : `${ops.p95Ms} ms`} l="API latency p95" />
        <Kpi v={ops.errorRatePct == null ? '—' : `${ops.errorRatePct}%`} l="Error rate (status ≥400)" />
        <Kpi v={ops.statusCount} l="Logged requests" />
      </Kpis>
      <p className="desc">Latency/error parsed from request log lines; thumbs-up/down, NPS/CSAT and NRR are not in the snapshot and need separate instrumentation.</p>
    </>
  );
}
